import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import CommentsAPI from "../../api/CommentsAPI";
import { DefaultFields } from "../../../types/GlobalInterfaces";
import { getErrorMessage } from "../../../utils/GeneralUtils";
import { CommentsQuery } from "./useFetchComments";

// type CommentContext = "Record" | "BrieGPT";

interface BaseCommentProps extends DefaultFields {
  ContextID: string;
}

export interface RecordComment extends BaseCommentProps {
  Context: "Record";
  Comment: { RichText: string; PlainText: string };
  ParentComment?: ParentComment;
  Reactions: { [reaction: string]: string[] };
  MentionedUsers: {
    id: string;
    _ModuleAPIName: string;
    _RecordName: string;
  }[];
  Deleted?: boolean;
}

export type Comment = RecordComment;

export interface ParentComment {
  _id: string;
  CreatedBy: string;
  Created: string;
  Comment: string;
}

export type ConvoMetadata = { RecordName: string; RecordModule: string };

interface Props {
  comment: {
    comment: { RichText: string; PlainText: string } | string;
    context: string;
    contextID: string;
    parentComment?: ParentComment;
    mentionedUsers: any;
    commentCount: number;
  };
  convoMetadata: ConvoMetadata;
  onSuccess: () => void;
  onError: (err: string) => void;
}

export default function useCreateComment() {
  const client = useQueryClient();

  const createComment = useMutation({
    mutationFn: ({ comment, convoMetadata }: Props) => {
      return CommentsAPI.createComment(comment, convoMetadata).then(
        (data) => data.data.data
      );
    },
    onSuccess: (data, { onSuccess, onError, comment }) => {
      const newComment: Comment | null = data.comment;

      // Handle when new comments were added, and the user hasn't seen them yet
      if (!newComment) {
        const freshPageOneData: CommentsQuery = data;

        onError(
          "New comments were added! Please review them before sending again"
        );
        return client.setQueryData<InfiniteData<CommentsQuery>>(
          ["comments", comment.context, comment.contextID],
          {
            pages: [
              {
                comments: freshPageOneData.comments,
                hasNextPage: freshPageOneData.hasNextPage,
                totalComments: freshPageOneData.totalComments,
                pageNumber: freshPageOneData.pageNumber,
                users: freshPageOneData.users,
                isASubscriber: true,
              },
            ],
            pageParams: [1],
          }
        );
      }

      client.setQueryData<InfiniteData<CommentsQuery>>(
        ["comments", newComment.Context, newComment.ContextID],
        (prev: any) => {
          prev = structuredClone(prev);

          if (prev?.pages?.[0]) {
            const newFirstPage = prev.pages[0];

            newFirstPage.comments.unshift(newComment);
            newFirstPage.totalComments++;

            return {
              pages: [{ ...newFirstPage, isASubscriber: true }],
              pageParams: [1],
            };
          } else {
            return {
              pages: [
                {
                  comments: [newComment],
                  hasNextPage: false,
                  totalComments: 1,
                  pageNumber: 1,
                  isASubscriber: true,
                },
              ],
              pageParams: [1],
            };
          }
        }
      );
      onSuccess();
    },
    onError: (err, { onError }) => {
      onError(getErrorMessage(err));
    },
  });

  return {
    createComment: createComment.mutate,
    creatingComment: createComment.isPending,
  };
}
